import { CSSResult, html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { consume } from '@lit/context';
import styles from './wishlist-app-push-teaser.scss?inline';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { Messages } from '../../../@types/wishlist.js';
import { CookieHandlerInstance } from '../../../scripts/service/cookiehandler.js';
import { openDeeplink } from '@mobile-app/native-dialog';
import close from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_close.svg?element';
import arrowRight from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-20_arrow-right.svg?element';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';

const DISMISS_COOKIE_NAME = 'ents_wishlist_apppush_dismissed';

@customElement('ents-wishlist-app-push-teaser')
export class WishlistAppPushTeaser extends LitElement {
  @consume({ context: i18nContext, subscribe: true })
  @property({ attribute: false })
  private messages!: Messages;

  @property({ type: Boolean, reflect: true })
  hidden: boolean = false;

  private readonly initialHiddenState: boolean = false;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  constructor() {
    super();

    this.initialHiddenState = this.getAttribute('hidden') !== null;
    this.onTabChange = this.onTabChange.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    if (CookieHandlerInstance.getCookie(DISMISS_COOKIE_NAME) !== '') {
      this.hidden = true;
    }

    document.addEventListener('ents:wishlist:tab:changed', this.onTabChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('ents:wishlist:tab:changed', this.onTabChange);
  }

  private onTabChange({ detail: { tab } }: CustomEvent<{ tab: 'favorites' | 'collections' | 'curated-collection' }>) {
    if (tab === 'favorites') {
      this.hidden = this.initialHiddenState || CookieHandlerInstance.getCookie(DISMISS_COOKIE_NAME) !== '';
    } else {
      // app push teaser is only shown inline on the favorites tab
      this.hidden = true;
    }
  }

  private readonly dismiss = () => {
    CookieHandlerInstance.setSessionCookie(DISMISS_COOKIE_NAME, 'dismissed');
    this.hidden = true;
  };

  private readonly activatePushNotifications = (e: Event) => {
    e.preventDefault();
    CookieHandlerInstance.setSessionCookie(DISMISS_COOKIE_NAME, 'dismissed');
    this.hidden = true;
    openDeeplink('breuningerapp://pushSettings');
  };

  render() {
    if (!this.messages) {
      return nothing;
    }

    return html`
      <div class="ents-app-push-teaser" data-test-ents-app-push-teaser>
        <button
          class="ents-app-push-teaser__close bdk-button-icon"
          size="extra-extra-small"
          variant="overlay-light"
          type="button"
          aria-label="${this.messages['ents_wishlist_actionmenu_close']}"
          @click="${this.dismiss}"
        >
          ${hideIconFromAssistiveTechnology(close())}
        </button>
        <p class="ents-app-push-teaser__title ents-kicker-medium">
          ${this.messages['ents_wishlist_app_push_teaser_title']}
        </p>
        <p class="ents-app-push-teaser__headline ents-heading ents-m:heading-medium">
          ${this.messages['ents_wishlist_app_push_teaser_headline']}
        </p>
        <p class="ents-app-push-teaser__body ents-copy">
          ${unsafeHTML(this.messages['ents_wishlist_app_push_teaser_body'])}
        </p>
        <a href="#" class="bdk-link-primary" @click="${this.activatePushNotifications}">
          ${this.messages['ents_wishlist_app_push_teaser_linktext']} ${hideIconFromAssistiveTechnology(arrowRight())}
        </a>
      </div>
    `;
  }
}
